import React from 'react';
import { connect } from 'react-redux';
import { useAsync } from 'react-use';
import cn from 'classnames';
import moment from 'moment';
import { bindActionCreators } from 'redux';

import IMG_PENCIL from 'assets/images/icons/pencil-note.svg';
import IMG_DOC from 'assets/images/menu/settings-menu/document-purple.svg';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import CheckboxInput from 'components/form/CheckboxInput.component';
import Foldout from 'components/generic/Foldout.component';
import Icon from 'components/generic/Icon.component';
import IconStatus from 'components/generic/IconStatus.component';
import LSK from 'constants/core/localStorageKey.const';
import S from 'constants/string.const';
import { withTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import { selectAccessHasLockToken } from 'selectors/access.selectors';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import { fromPropsToMatchedSalonId } from 'utils/converters/props/bl.util';
import lsGet from 'utils/dom/storage/lsGet.util';
import lsSet from 'utils/dom/storage/lsSet.util';
import logger from 'utils/logger.util';
import C from 'utils/values/constant.util';

import PopupModalContainer from 'common/PopupModal';
import Spinner from 'common/Spinner';
import CategoriesScreenContainer from 'onboarding/categories/CategoriesScreen';
import { getRoles } from 'onboarding/roles/actions';
import {
  addCategory,
  addService,
  clearFutureAppointmentsData,
  clearRequestError,
  deleteCategory,
  deleteService,
  editCategory,
  editService,
  getCategories,
  getFutureAppointmentsWithService,
  removeSavedCategoriesIds,
  removeSavedServicesIds,
} from 'onboarding/services/actions';
import ServicesDescription from 'settings/services/ServicesDescription.component';

import CN from './ServicesLayout.module.scss';

const L = logger('ServicesLayout'); // eslint-disable-line no-unused-vars

const USE_SERVICES_DESCRIPTION = true;

const idOf = (object) => object && object.id;

const translateDescription = (t) => C([t('staffAndBusiness.servicePrices.descriptionPart1'), t('staffAndBusiness.servicePrices.descriptionPart2')].join(S.space));

// settingsSavedCategoriesTimerIds map keeps all the ids of the timers that have been started for specific category that has been just saved
// the key is the id of the category and the value is the id of the timer that should be cleared

class ServicesLayout extends React.Component {
  constructor(props) {
    super(props);
    const expandedCategoriesIDsStr = lsGet(LSK.expandedCatsIds);
    let expandedCategoriesIDs = [];
    if (expandedCategoriesIDsStr) {
      expandedCategoriesIDs = expandedCategoriesIDsStr.split(',').map((e) => parseInt(e, 10));
    }
    this.state = {
      addCategoryExpanded: false,
      errorMessage: '',
      isPostingNewCategory: false,
      settingsSavedCategoriesTimerIds: [],
      settingsSavedServicesTimerIds: [],
      expandedCategoriesIDs,
      editingCategoriesIDs: [],
    };
    L.debug('constructor()', { props, state: this.state });
  }

  UNSAFE_componentWillMount() {
    const { getCategories, getRoles } = this.props;
    const salonId = fromPropsToMatchedSalonId(this.props);

    const getCategoriesCalled = lsGet(LSK.calledCats);
    if (getCategoriesCalled) return;

    L.unignore('UNSAFE_componentWillMount()', 'getCategories:', getCategories(salonId));
    L.unignore('UNSAFE_componentWillMount()', 'getRoles:', getRoles(salonId));

    lsSet(LSK.calledCats, 'true');
    setTimeout(() => void lsSet(LSK.calledCats, ''), 24 * 60 * 60 * 1000); // ?
  }

  // noinspection JSCheckFunctionSignatures
  UNSAFE_componentWillReceiveProps(nextProps) {
    L.debug('constructor()', { nextProps, props: this.props, state: this.state });
    // Handle the categories that have been marked as saved with the last request
    const differenceCategories = nextProps.settingsSavedCategoriesIDs.filter((categorySaved) => categorySaved.isNew).map((categorySaved) => categorySaved.id);

    const settingsSavedCategoriesTimerIds = this.state.settingsSavedCategoriesTimerIds.slice(0);

    if (differenceCategories.length) {
      const timerId = window.setTimeout(() => {
        L.debug('componentWillReceiveProps()', 'timerId:', timerId, 'differenceCategories:', differenceCategories);
        this.props.removeSavedCategoriesIds(differenceCategories);
        differenceCategories.forEach((id) => {
          settingsSavedCategoriesTimerIds.splice(id, 1);
        });
      }, 2000);

      differenceCategories.forEach((id) => {
        window.clearTimeout(settingsSavedCategoriesTimerIds[id]);
        settingsSavedCategoriesTimerIds[id] = timerId;
      });
    }
    // Handle the services that have been marked as saved in the last requests
    const differenceServices = nextProps.settingsSavedServicesIDs.filter((serviceSaved) => serviceSaved.isNew).map((serviceSaved) => serviceSaved.id);

    const settingsSavedServicesTimerIds = this.state.settingsSavedServicesTimerIds.slice(0);

    if (differenceServices.length) {
      const timerId = window.setTimeout(() => {
        L.debug('componentWillReceiveProps()', 'timerId:', timerId, 'differenceServices:', differenceServices);
        this.props.removeSavedServicesIds(differenceServices);
        differenceServices.forEach((id) => {
          settingsSavedServicesTimerIds.splice(id, 1);
        });
      }, 2000);

      differenceServices.forEach((id) => {
        window.clearTimeout(settingsSavedServicesTimerIds[id]);
        settingsSavedServicesTimerIds[id] = timerId;
      });
    }

    if (this.state.isPostingNewCategory && !nextProps.isPosting) {
      this.setState({ addCategoryExpanded: false, isPostingNewCategory: false });
    }

    this.setState({
      settingsSavedCategoriesTimerIds,
      settingsSavedServicesTimerIds,
      errorMessage: nextProps.requestErrorMessage,
    });

    // handle editing services that are checked for having any future appoinments with them
    // but in fact they don't have => we should proceed to the final state of the editing
    if (
      this.state.serviceToEdit &&
      !nextProps.futureAppointments.isFetching &&
      nextProps.futureAppointments.response &&
      nextProps.futureAppointments.response.default_priced === 0 &&
      nextProps.futureAppointments.response.custom_priced === 0
    ) {
      this.finishEditService();
    }
  }

  onDeleteCategoryClick = (category) => {
    const salonId = fromPropsToMatchedSalonId(this.props);
    if (category.id && category.services.length === 0 && this.props.categories.length > 1) {
      this.props.deleteCategory(category.id, salonId);
    } else if (category.services.length !== 0) {
      this.setState({
        errorMessage: this.props.t('staffAndBusiness.servicePrices.deleteCatErrorMsg1'),
      });
    } else if (this.props.categories.length === 1) {
      this.setState({ errorMessage: this.props.t('staffAndBusiness.servicePrices.deleteCatErrorMsg2') });
    }

    this.setState({ categoryToDelete: undefined });
  };

  onOpenCategoryClick = (category) => {
    const expandedCategoriesIDs = this.state.expandedCategoriesIDs.slice(0);

    if (!category.id) return;

    const catIndx = expandedCategoriesIDs.indexOf(category.id);

    if (catIndx === -1 && category.id) {
      expandedCategoriesIDs.push(category.id);
    } else {
      expandedCategoriesIDs.splice(catIndx, 1);
    }

    this.setState({ expandedCategoriesIDs });
  };

  onEditCategoryClick = (category) => {
    const editingCategoriesIDs = this.state.editingCategoriesIDs.slice(0);
    if (!category.id) return;

    const editCatIndx = editingCategoriesIDs.indexOf(category.id);

    if (editCatIndx === -1 && category.id) {
      editingCategoriesIDs.push(category.id);
    } else {
      editingCategoriesIDs.splice(editCatIndx, 1);
    }

    this.setState({ editingCategoriesIDs });
  };

  onSaveCategoryClick = (category) => {
    const salonId = fromPropsToMatchedSalonId(this.props);

    this.props.editCategory(category, salonId);
    this.onEditCategoryClick(category);
  };

  onEditServiceSave = (service, differentPricesRolesIds, originalRoleIds) => {
    const salonId = fromPropsToMatchedSalonId(this.props);

    this.props.clearRequestError();
    this.setState({ errorMessage: '' });

    if (differentPricesRolesIds.length) {
      const today = moment().startOf('day').toISOString();
      this.props.getFutureAppointmentsWithService(salonId, service.id || 0, differentPricesRolesIds, today);
      this.setState({
        serviceToEdit: {
          service,
          originalRoleIds,
          options: {
            update_related_custom_priced_procedures: false,
            update_related_default_priced_procedures: false,
            update_procedures_older_than: today,
          },
        },
      });
    } else {
      this.props.editService(service, salonId, undefined, originalRoleIds);
    }
  };

  onDeleteService = (serviceId) => {
    const salonId = fromPropsToMatchedSalonId(this.props);
    this.props.deleteService(salonId, serviceId);
  };

  onCheckboxClick = (valueToChange) => {
    const changeState = { serviceToEdit: { ...this.state.serviceToEdit } };
    changeState.serviceToEdit.options[valueToChange] = !changeState.serviceToEdit.options[valueToChange];
    this.setState(changeState);
  };

  onDefaultPriceClick = () => {
    this.onCheckboxClick('update_related_default_priced_procedures');
  };

  onCustomPriceClick = () => {
    this.onCheckboxClick('update_related_custom_priced_procedures');
  };

  cancelEditService = () => {
    this.props.clearFutureAppointmentsData();
    this.setState({ serviceToEdit: null });
  };

  finishEditService = () => {
    const { serviceToEdit } = this.state;
    if (!serviceToEdit) return;

    const salonId = fromPropsToMatchedSalonId(this.props);
    const { editService, clearFutureAppointmentsData } = this.props;
    const { options, service, originalRoleIds } = serviceToEdit;

    this.setState({ serviceToEdit: null });
    clearFutureAppointmentsData();
    editService(service, salonId, options, originalRoleIds);
  };

  hideError = () => {
    this.setState({ errorMessage: '' });
    this.props.clearRequestError();
  };

  render() {
    const { editingCategoriesIDs, addCategoryExpanded, categoryToDelete, expandedCategoriesIDs, serviceToEdit, errorMessage } = this.state;

    const { settingsSavedServicesIDs, settingsSavedCategoriesIDs, addCategory, isPosting, settingsErrorCategoriesIDs, addService, settingsErrorServicesIDs, futureAppointments, isDarkMode, t } =
      this.props;

    const salonId = fromPropsToMatchedSalonId(this.props);

    return (
      <div
        data-bem="ServicesLayout"
        className={cn({
          services: true,
          [CN.component]: true,
          [CN.dark]: isDarkMode,
        })}
      >
        {errorMessage && <PopupModalContainer popupTitle={t('common.error')} popupMessage={errorMessage} buttonText={t('common.buttons.okay')} buttonCallback={this.hideError} />}

        {fromAnyToBool(categoryToDelete) && (
          <SalonModal tag="ServicesLayout__delete-category" isOpen withFooter>
            <SalonModal.Body>
              <div className={CN.imageModal}>
                <IconStatus variant="alert">
                  <Icon variant="alert" />
                </IconStatus>
              </div>
              <div className={CN.contentModal}>
                <div className={CN.titleModal}>{t('staffAndBusiness.servicePrices.deleteCatModalTitle')}</div>
                <span className={CN.textModal}>
                  {t('staffAndBusiness.servicePrices.deleteCatModalText')}
                  <span className={CN.categoryName}>{categoryToDelete.name}</span>?
                </span>
              </div>
            </SalonModal.Body>
            <SalonModal.Footer>
              <Button variant="slim" onClick={() => void this.setState({ categoryToDelete: null })}>
                {t('common.buttons.cancelUpp')}
              </Button>
              <Button
                variant="primaryForce"
                onClick={() => {
                  if (categoryToDelete) {
                    this.onDeleteCategoryClick(categoryToDelete);
                  } else {
                    L.debug('No categoryToDelete:', categoryToDelete, 'aborting delete...');
                  }
                }}
              >
                {t('common.buttons.confirmUpp')}
              </Button>
            </SalonModal.Footer>
          </SalonModal>
        )}

        {futureAppointments.isFetching && <Spinner color="white" message={t('staffAndBusiness.servicePrices.loadingSpinnerText')} />}

        {serviceToEdit && serviceToEdit.options && (
          <SalonModal tag="ServicesLayoutLoader" isOpen={futureAppointments && futureAppointments.response} withFooter>
            <SalonModal.Header>
              <div className={CN.closeButtonContainer}>
                <Button variant="empty" onClick={this.cancelEditService}>
                  <Icon variant="close" variantInvert />
                </Button>
              </div>
            </SalonModal.Header>
            <SalonModal.Body>
              <IconStatus variant="info" width="lg" position="left">
                <Icon variant="calendarEmpty" selected width="l1" />
              </IconStatus>
              <div className={CN.contentModal}>
                <div className={CN.titleLeftModal}>{t('staffAndBusiness.servicePrices.updatePriceModal.title')}</div>
              </div>
              <div className={CN.textModal}>
                {t('staffAndBusiness.servicePrices.updatePriceModal.subTitle')}
                <p>
                  <span className={CN.focused}>{t('staffAndBusiness.servicePrices.updatePriceModal.important')}</span> {t('staffAndBusiness.servicePrices.updatePriceModal.alertMsg')}
                </p>

                {!!(futureAppointments.response && futureAppointments.response.default_priced) && (
                  <div className={CN.space}>
                    <CheckboxInput variant="tick" checked={serviceToEdit.options.update_related_default_priced_procedures} onClick={this.onDefaultPriceClick}>
                      {t('staffAndBusiness.servicePrices.updatePriceModal.updateAll')}
                      <span className={CN.textBold}>{futureAppointments.response && futureAppointments.response.default_priced}</span>
                      {t('staffAndBusiness.servicePrices.updatePriceModal.upcomingBookings')}
                    </CheckboxInput>
                  </div>
                )}
                {!!(futureAppointments.response && futureAppointments.response.custom_priced) && (
                  <CheckboxInput variant="tick" checked={serviceToEdit.options.update_related_custom_priced_procedures} onClick={this.onCustomPriceClick}>
                    {t('staffAndBusiness.servicePrices.updatePriceModal.updateAll')}
                    <span className={CN.textBold}>{futureAppointments.response && futureAppointments.response.custom_priced}</span>
                    {t('staffAndBusiness.servicePrices.updatePriceModal.customBookings')}
                  </CheckboxInput>
                )}
              </div>
            </SalonModal.Body>
            <SalonModal.Footer>
              <Button variant="outline" onClick={this.finishEditService} xSpace>
                {t('staffAndBusiness.servicePrices.updatePriceModal.continueButton')}
              </Button>
              <Button
                variant="primaryForce"
                onClick={this.finishEditService}
                xSpace
                disabled={!serviceToEdit.options.update_related_default_priced_procedures && !serviceToEdit.options.update_related_custom_priced_procedures}
              >
                {t('common.buttons.updateUpp')}
              </Button>
            </SalonModal.Footer>
          </SalonModal>
        )}

        <Foldout isOpen title={t('staffAndBusiness.servicePrices.foldoutTitle')} src={IMG_DOC}>
          <div data-bem="ServicesLayout__subTitle" className={CN.subTitle}>
            {translateDescription(t)}
          </div>
          <div data-bem="ServicesLayout__container" className={CN.containerServices}>
            <CategoriesScreenContainer
              expandedCategoriesIDs={expandedCategoriesIDs}
              editingCategoriesIDs={editingCategoriesIDs}
              savedCategoriesIDs={settingsSavedCategoriesIDs.map(idOf)}
              errorCategoriesData={settingsErrorCategoriesIDs}
              savedServicesIDs={settingsSavedServicesIDs.map(idOf)}
              errorServicesData={settingsErrorServicesIDs}
              onOpenClick={this.onOpenCategoryClick}
              onEditClick={this.onEditCategoryClick}
              onDeleteClick={(category) => void this.setState({ categoryToDelete: category })}
              onEditServiceSave={this.onEditServiceSave}
              onDeleteServiceClick={this.onDeleteService}
              isAddCategoryExpanded={addCategoryExpanded}
              onAddNewCategoryClick={() => void this.setState({ addCategoryExpanded: !addCategoryExpanded })}
              onSaveNewCategoryBtnClick={(category) => {
                this.setState({ isPostingNewCategory: true });
                addCategory(category.name, category.color, salonId);
              }}
              onAddNewService={(service, category) => {
                if (category.id) {
                  addService(service, category.id, salonId);
                }
              }}
              isPostRequestSent={isPosting}
              onSaveClick={this.onSaveCategoryClick}
            />
          </div>
        </Foldout>

        {USE_SERVICES_DESCRIPTION && (
          <Foldout isOpen={false} title={t('onlineBooking.generalSettings.serviceDescription.foldoutTitle')} src={IMG_PENCIL}>
            <ServicesDescription />
          </Foldout>
        )}
      </div>
    );
  }
}

const ServicesLayoutLoader = (props) => {
  const salonId = useCurrentSalonId();
  const theme = useTheme();
  const { getCategories, hasLockToken } = props;

  const {
    value: valueCategories,
    loading: isLoadingCategories,
    error: errorCategories,
  } = useAsync(async () => {
    if (!salonId) return;
    if (!hasLockToken) return; // it's a pin-safe call to BE, so it will display an error if the token is not there
    return await getCategories(salonId);
  }, [getCategories, hasLockToken, salonId]);

  if (errorCategories) {
    L.warn('ServicesLayoutLoader()', { valueCategories, isLoadingCategories, errorCategories });
  }

  return <ServicesLayout {...props} {...theme} salonId={salonId} />;
};

export const mapStateToProps = (state) => ({
  hasLockToken: selectAccessHasLockToken(state),
  categories: state.onboarding.services.categories,
  requestErrorMessage: state.onboarding.services.errorMessage,
  futureAppointments: state.onboarding.services.changePrice.futureAppointments,
  isFetching: state.onboarding.services.isFetching,
  isPosting: state.onboarding.services.isPosting,
  isSaved: state.onboarding.services.isSaved,
  settingsSavedCategoriesIDs: state.onboarding.services.settingsSavedCategoriesIDs,
  settingsErrorCategoriesIDs: state.onboarding.services.settingsErrorCategoriesIDs,
  settingsSavedServicesIDs: state.onboarding.services.settingsSavedServicesIDs,
  settingsErrorServicesIDs: state.onboarding.services.settingsErrorServicesIDs,
});

export const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getCategories,
      getRoles,
      addCategory,
      addService,
      editCategory,
      removeSavedCategoriesIds,
      removeSavedServicesIds,
      editService,
      deleteService,
      deleteCategory,
      getFutureAppointmentsWithService,
      clearFutureAppointmentsData,
      clearRequestError,
    },
    dispatch,
  );

const ServicesLayoutHoc = connect(mapStateToProps, mapDispatchToProps)(withTranslation()(ServicesLayoutLoader));

export default ServicesLayoutHoc;
